import { AddIcon, BenchmarkLinkIcon, CloseIcon } from "@/components/icons";
import { ensureProtocol } from "@/utils/ensureProtocol";
import { Popover, Tooltip } from "antd";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

type BenchmarkListItem = { url: string; name: string };

function LinkChip({
  url,
  name,
  className,
}: {
  url: string;
  name: string;
  className?: string;
}) {
  return (
    <a
      href={ensureProtocol(url)}
      target="_blank"
      rel="noreferrer noopener"
      className={
        className ??
        "border border-[#C7D2FE] bg-[#EEF2FF] rounded-[6px] flex items-center h-[22px] px-2 w-fit gap-[6px] flex-shrink-0 max-w-full no-underline hover:opacity-90"
      }
      onClick={(event) => event.stopPropagation()}
    >
      <BenchmarkLinkIcon className="text-[#6366F1] hover:text-[#4338CA] flex-shrink-0" />
      <span className="text-[#4338CA] text-[12px] font-medium font-['Inter'] leading-5 max-w-[140px] overflow-hidden text-ellipsis whitespace-nowrap">
        {name || url}
      </span>
    </a>
  );
}

export default function BenchmarkTags({
  type,
  url,
  name,
  onClick,
  list,
  className,
}: {
  type: "add" | "close" | "multiple";
  url?: string;
  name?: string;
  onClick?: () => void;
  list?: BenchmarkListItem[];
  className?: string;
}) {
  const { t } = useTranslation();
  const nameRef = useRef<HTMLDivElement>(null);
  const [tooltipTitle, setTooltipTitle] = useState("");

  const multipleItems = useMemo(() => {
    if (Array.isArray(list) && list.length > 0) {
      return list
        .map((item) => ({
          url: String(item?.url ?? "").trim(),
          name: String(item?.name ?? item?.url ?? "").trim(),
        }))
        .filter((item) => item.url);
    }
    const fallback = String(url ?? "").trim();
    if (!fallback) return [];
    return [
      {
        url: fallback,
        name: String(name ?? fallback).trim() || fallback,
      },
    ];
  }, [list, url, name]);

  if (type === "multiple") {
    const first = multipleItems[0];
    if (!first) return null;
    const extraCount = multipleItems.length - 1;

    return (
      <div className={`border border-[#C7D2FE] bg-[#EEF2FF] rounded-[6px] flex items-center h-[22px] px-2 w-fit gap-[6px] flex-shrink-0 max-w-full ${className}`}>
        <span className="text-[#6366F1] text-[12px] font-medium font-['Inter'] leading-5 whitespace-nowrap">
          {t("campaignCreate.v4SettingsProfiles.matchedFromPrefix", {
            defaultValue: "Matched from",
          })}
        </span>
        <a
          href={ensureProtocol(first.url)}
          className="cursor-pointer flex items-center gap-[6px] min-w-0 no-underline"
          target="_blank"
          rel="noreferrer noopener"
          onClick={(event) => event.stopPropagation()}
        >
          <BenchmarkLinkIcon className="text-[#6366F1] hover:text-[#4338CA] flex-shrink-0" />
          <Tooltip title={first.name} mouseEnterDelay={0.1}>
            <span className="text-[#4338CA] text-[12px] font-medium font-['Inter'] leading-5 max-w-[140px] overflow-hidden text-ellipsis whitespace-nowrap">
              {first.name}
            </span>
          </Tooltip>
        </a>
        {extraCount > 0 ? (
          <Popover
            trigger="hover"
            placement="bottomLeft"
            content={
              <div className="max-w-[420px] flex flex-wrap gap-2">
                {multipleItems.slice(1).map((item) => (
                  <LinkChip
                    key={item.url}
                    url={item.url}
                    name={item.name}
                  />
                ))}
              </div>
            }
          >
            <button
              type="button"
              className="h-[16px] flex items-center px-1 rounded-[4px] bg-[#C7D2FE] text-[#4338CA] text-[12px] font-medium font-['Inter'] cursor-pointer border-0 flex-shrink-0"
              onClick={(event) => event.stopPropagation()}
            >
              +{extraCount}
            </button>
          </Popover>
        ) : null}
      </div>
    );
  }

  return (
    <div className="border border-[#C7D2FE] bg-[#EEF2FF] rounded-[6px] flex items-center h-[22px] px-2 w-fit gap-[6px] flex-shrink-0 max-w-full">
      <a
        href={ensureProtocol(url)}
        className="cursor-pointer flex-shrink-0"
        target="_blank"
        rel="noreferrer noopener"
        onClick={(event) => event.stopPropagation()}
      >
        <BenchmarkLinkIcon className="text-[#6366F1] hover:text-[#4338CA]" />
      </a>
      <div
        className="cursor-pointer flex items-center gap-[6px]"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onClick?.();
        }}
      >
        <Tooltip
          mouseEnterDelay={0.1}
          mouseLeaveDelay={0.3}
          title={
            type === "add" ? (
              <a
                href={ensureProtocol(url)}
                target="_blank"
                rel="noreferrer noopener"
                className="text-white underline break-all"
                onClick={(event) => event.stopPropagation()}
              >
                {url}
              </a>
            ) : (
              tooltipTitle
            )
          }
        >
          <div
            ref={nameRef}
            onMouseEnter={() => {
              if (type !== "close") return;
              setTooltipTitle(
                (nameRef.current?.scrollWidth ?? 0) >
                  (nameRef.current?.clientWidth ?? 0)
                  ? name ?? ""
                  : "",
              );
            }}
            className="text-[#4338CA] text-sm font-medium font-['Inter'] leading-5 max-w-[140px] overflow-hidden text-ellipsis whitespace-nowrap"
          >
            {name}
          </div>
        </Tooltip>
        <button
          type="button"
          className="cursor-pointer flex-shrink-0 p-0 border-0 bg-transparent leading-none"
          aria-label={type === "close" ? "Remove" : "Add"}
        >
          {type === "close" ? (
            <CloseIcon className="text-[#6366F1] hover:text-[#4338CA] w-4 h-4" />
          ) : (
            <AddIcon className="text-[#6366F1] hover:text-[#4338CA] w-4 h-4" />
          )}
        </button>
      </div>
    </div>
  );
}
